import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { useToast } from '../../ui/Toast';
import { PageHead } from '../../shell/PageHead';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';

/**
 * /docs — documentação viva da API consumidor (flows §10).
 * Super seleciona uma empresa → exemplos curl com a apikey real pré-preenchida.
 * Download é URL-capacidade (sem apikey) — ADR-0018.
 */
interface CompanyItem {
  id: string;
  nome: string;
  apikey_ativa: string | null;
}

const BASE = typeof window !== 'undefined' ? window.location.origin : '';

function CodeBlock({ code }: { code: string }) {
  const toast = useToast();
  return (
    <div style={{ position: 'relative', marginBottom: 14 }}>
      <pre
        className="mono"
        style={{
          background: 'var(--bg-0)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-md)',
          padding: '14px 16px',
          fontSize: 12.5,
          lineHeight: 1.6,
          overflowX: 'auto',
          color: 'var(--text-1)',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all'
        }}
      >
        {code}
      </pre>
      <button
        className="focusable"
        onClick={() => {
          navigator.clipboard?.writeText(code).then(
            () => toast('Copiado!', 'success'),
            () => toast('Não foi possível copiar', 'danger')
          );
        }}
        style={{
          position: 'absolute',
          top: 8,
          right: 8,
          fontSize: 11.5,
          fontWeight: 600,
          padding: '4px 10px',
          borderRadius: 'var(--r-sm)',
          border: '1px solid var(--border)',
          background: 'var(--bg-2)',
          color: 'var(--text-1)',
          cursor: 'pointer'
        }}
      >
        copiar
      </button>
    </div>
  );
}

// =====================================================================
// Console interativo — método + path + query + body editáveis,
// botão Executar, exibe HTTP status + tempo + body formatado.
// =====================================================================

interface ExecResult {
  status: number;
  time_ms: number;
  body: any;
  raw_text: string;
  is_binary: boolean;
  url: string;
}

const TEMPLATES: Array<{
  id: string;
  label: string;
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  path: string;
  query: string;
  body: string;
}> = [
  { id: 'health', label: 'Healthcheck', method: 'GET', path: '/healthz', query: '', body: '' },
  {
    id: 'search_auto',
    label: 'Search · ranker=auto',
    method: 'GET',
    path: '/v1/search/',
    query: 'keywords=praia&tipo=image&quantidade=3',
    body: ''
  },
  {
    id: 'search_local',
    label: 'Search · ranker=local_only',
    method: 'GET',
    path: '/v1/search/',
    query: 'keywords=praia&tipo=image&ranker=local_only',
    body: ''
  },
  {
    id: 'search_ai',
    label: 'Search · ranker=ai_only (degrada)',
    method: 'GET',
    path: '/v1/search/',
    query: 'keywords=praia&tipo=image&ranker=ai_only',
    body: ''
  },
  {
    id: 'search_off',
    label: 'Search · ranker=off',
    method: 'GET',
    path: '/v1/search/',
    query: 'keywords=praia&tipo=image&ranker=off',
    body: ''
  },
  {
    id: 'search_audio',
    label: 'Search · tipo=audio',
    method: 'GET',
    path: '/v1/search/',
    query: 'keywords=praia&tipo=audio&quantidade=1',
    body: ''
  },
  {
    id: 'search_hr',
    label: 'Search · human_revision=true',
    method: 'GET',
    path: '/v1/search/',
    query: 'keywords=praia&tipo=image&human_revision=true',
    body: ''
  },
  {
    id: 'download',
    label: 'Download (substitua UUID)',
    method: 'GET',
    path: '/v1/files/SUBSTITUA-PELO-UUID/download',
    query: '',
    body: ''
  }
];

function ConsoleSection({ apikey }: { apikey: string }) {
  const toast = useToast();
  const [templateId, setTemplateId] = useState<string>('search_auto');
  const [method, setMethod] = useState<'GET' | 'POST' | 'PATCH' | 'DELETE'>('GET');
  const [path, setPath] = useState('/v1/search/');
  const [query, setQuery] = useState('keywords=praia&tipo=image&quantidade=3');
  const [body, setBody] = useState('');
  const [response, setResponse] = useState<ExecResult | null>(null);
  const [executing, setExecuting] = useState(false);

  function applyTemplate(id: string) {
    const t = TEMPLATES.find((x) => x.id === id);
    if (!t) return;
    setTemplateId(id);
    setMethod(t.method);
    setPath(t.path);
    setQuery(t.query);
    setBody(t.body);
    setResponse(null);
  }

  const noApikey = !apikey || apikey === '<SUA_APIKEY>';
  const skipApikey = path.startsWith('/healthz');

  const fullUrl = useMemo(() => {
    return query ? `${path}?${query}` : path;
  }, [path, query]);

  const curlCmd = useMemo(() => {
    let cmd = `curl "${BASE}${fullUrl}"`;
    if (method !== 'GET') cmd += ` \\\n  -X ${method}`;
    if (!skipApikey && !noApikey) cmd += ` \\\n  -H "X-Api-Key: ${apikey}"`;
    if (body) cmd += ` \\\n  -H "Content-Type: application/json" \\\n  -d '${body.replace(/'/g, `'\\''`)}'`;
    return cmd;
  }, [method, fullUrl, body, apikey, skipApikey, noApikey]);

  async function execute() {
    if (executing) return;
    setExecuting(true);
    setResponse(null);
    // Usa o proxy do Vite: relative path → backend :3000
    const init: RequestInit = {
      method,
      credentials: 'omit',
      headers: {
        Accept: 'application/json',
        ...(!skipApikey && !noApikey ? { 'X-Api-Key': apikey } : {}),
        ...(body ? { 'Content-Type': 'application/json' } : {})
      }
    };
    if (body) init.body = body;
    const t0 = Date.now();
    try {
      const r = await fetch(fullUrl, init);
      const time_ms = Date.now() - t0;
      const ct = r.headers.get('content-type') ?? '';
      const isBinary = !ct.includes('json') && !ct.includes('text');
      let raw = '';
      let parsed: any = null;
      if (isBinary) {
        raw = `[binário: ${ct}, ${r.headers.get('content-length') ?? '?'} bytes]`;
        parsed = raw;
      } else {
        raw = await r.text();
        if (raw && ct.includes('json')) {
          try {
            parsed = JSON.parse(raw);
          } catch {
            parsed = raw;
          }
        } else {
          parsed = raw;
        }
      }
      setResponse({ status: r.status, time_ms, body: parsed, raw_text: raw, is_binary: isBinary, url: fullUrl });
    } catch (err: any) {
      setResponse({
        status: 0,
        time_ms: Date.now() - t0,
        body: { error: String(err.message ?? err) },
        raw_text: String(err.message ?? err),
        is_binary: false,
        url: fullUrl
      });
    } finally {
      setExecuting(false);
    }
  }

  function copyCurl() {
    navigator.clipboard
      ?.writeText(curlCmd)
      .then(() => toast('curl copiado', 'info'))
      .catch(() => toast('Falha ao copiar', 'danger'));
  }

  const statusColor = (s: number): 'success' | 'warning' | 'danger' | 'neutral' =>
    s === 0 ? 'danger' : s < 300 ? 'success' : s < 400 ? 'warning' : 'danger';

  return (
    <Section id="console" title="Console (testar)">
      <p style={{ marginBottom: 14 }}>
        Edite os campos abaixo e clique <b>Executar</b>. As chamadas usam a apikey
        selecionada acima e o backend em <code className="mono">{BASE}</code>.
      </p>

      {noApikey && (
        <div
          style={{
            background: 'var(--warning-bg)',
            color: 'var(--warning)',
            border: '1px solid color-mix(in oklch, var(--warning) 30%, transparent)',
            borderRadius: 'var(--r-sm)',
            padding: '8px 12px',
            fontSize: 12.5,
            marginBottom: 12
          }}
        >
          Sem apikey selecionada — somente <code className="mono">/healthz</code> vai funcionar. Crie uma empresa em <b>Empresas</b> ou selecione uma no topo.
        </div>
      )}

      {/* Presets */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
        {TEMPLATES.map((t) => {
          const active = t.id === templateId;
          return (
            <button
              key={t.id}
              onClick={() => applyTemplate(t.id)}
              style={{
                fontSize: 11.5,
                padding: '4px 9px',
                borderRadius: 'var(--r-sm)',
                border: '1px solid ' + (active ? 'var(--brand)' : 'var(--border)'),
                background: active ? 'var(--brand-tint)' : 'var(--bg-1)',
                color: active ? 'var(--brand-strong)' : 'var(--text-1)',
                cursor: 'pointer',
                transition: 'all 0.12s var(--ease)'
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Builder */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Row label="Method">
          <select
            value={method}
            onChange={(e) => setMethod(e.target.value as any)}
            className="focusable mono"
            style={{ ...consoleInputStyle, width: 110, cursor: 'pointer' }}
          >
            {(['GET', 'POST', 'PATCH', 'DELETE'] as const).map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </Row>
        <Row label="Path">
          <input
            value={path}
            onChange={(e) => setPath(e.target.value)}
            className="focusable mono"
            style={consoleInputStyle}
            spellCheck={false}
          />
        </Row>
        <Row label="Query">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="keywords=praia&tipo=image"
            className="focusable mono"
            style={consoleInputStyle}
            spellCheck={false}
          />
        </Row>
        {(method === 'POST' || method === 'PATCH') && (
          <Row label="Body">
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder='{ "campo": "valor" }'
              className="focusable mono"
              style={{ ...consoleInputStyle, minHeight: 80, resize: 'vertical' }}
              spellCheck={false}
            />
          </Row>
        )}
      </div>

      <div
        style={{
          fontSize: 11.5,
          color: 'var(--text-3)',
          fontFamily: 'var(--font-mono)',
          padding: '6px 10px',
          background: 'var(--bg-0)',
          border: '1px dashed var(--border)',
          borderRadius: 'var(--r-sm)',
          marginTop: 10,
          wordBreak: 'break-all'
        }}
      >
        {method} {fullUrl}
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
        <Button variant="brand" icon="play" onClick={execute} disabled={executing}>
          {executing ? 'Executando…' : 'Executar'}
        </Button>
        <Button variant="default" icon="copy" onClick={copyCurl}>
          Copiar curl
        </Button>
        {response && (
          <Button variant="ghost" size="sm" onClick={() => setResponse(null)}>
            Limpar
          </Button>
        )}
      </div>

      {response && (
        <div style={{ marginTop: 16 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginBottom: 6
            }}
          >
            <Badge color={statusColor(response.status)} dot>
              {response.status === 0 ? 'erro de conexão' : `HTTP ${response.status}`}
            </Badge>
            <span className="mono tnum" style={{ fontSize: 12, color: 'var(--text-3)' }}>
              {response.time_ms}ms
            </span>
            {response.is_binary && (
              <Badge color="neutral" size="sm">
                binário
              </Badge>
            )}
            <span
              className="mono"
              style={{
                fontSize: 11,
                color: 'var(--text-3)',
                marginLeft: 'auto',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 240
              }}
              title={response.url}
            >
              {response.url}
            </span>
          </div>
          <pre
            style={{
              background: 'var(--bg-0)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--r-md)',
              padding: '12px 14px',
              fontSize: 11.5,
              lineHeight: 1.55,
              fontFamily: 'var(--font-mono)',
              color: 'var(--text-1)',
              margin: 0,
              maxHeight: 360,
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }}
          >
            {typeof response.body === 'string'
              ? response.body
              : JSON.stringify(response.body, null, 2)}
          </pre>
        </div>
      )}
    </Section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '70px 1fr', alignItems: 'center', gap: 10 }}>
      <span className="eyebrow" style={{ color: 'var(--text-3)', textAlign: 'right' }}>
        {label}
      </span>
      {children}
    </div>
  );
}

const consoleInputStyle: CSSProperties = {
  width: '100%',
  padding: '6px 10px',
  fontSize: 12.5,
  background: 'var(--bg-0)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--r-sm)',
  color: 'var(--text-0)',
  outline: 'none'
};

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} style={{ marginBottom: 30, scrollMarginTop: 16 }}>
      <h2 style={{ fontSize: 17, marginBottom: 10, color: 'var(--text-0)' }}>{title}</h2>
      {children}
    </section>
  );
}

export function Docs() {
  const { apiFetch, user } = useAuth();
  const [companies, setCompanies] = useState<CompanyItem[]>([]);
  const [companyId, setCompanyId] = useState<string>('');
  const isSuper = user?.papel === 'super_admin';

  useEffect(() => {
    if (!isSuper) return;
    apiFetch<{ items: CompanyItem[] }>('/v1/admin/companies/?limit=100').then((r) => {
      if (r.ok && r.data) {
        const withKey = r.data.items.filter((c) => c.apikey_ativa);
        setCompanies(withKey);
        if (withKey[0]) setCompanyId(withKey[0].id);
      }
    });
  }, [apiFetch, isSuper]);

  const apikey = useMemo(() => {
    const c = companies.find((x) => x.id === companyId);
    return c?.apikey_ativa ?? '<SUA_APIKEY>';
  }, [companies, companyId]);

  const searchCurl = `curl "${BASE}/v1/search/?keywords=praia&tipo=image&quantidade=1&ranker=auto" \\
  -H "X-Api-Key: ${apikey}"`;

  const searchResp = `{
  "files": [
    {
      "id": "uuid",
      "tipo": "image",
      "mime": "image/jpeg",
      "descricao": "Pôr do sol em uma praia tropical",
      "url_download": "/v1/files/<id>/download",
      "keywords": ["praia", "pôr do sol"]
    }
  ],
  "resultado": "cache_hit",
  "ranking_strategy": { "layer": "local", "escalated": false, "degraded_from": null }
}`;

  const filtersCurl = `curl "${BASE}/v1/search/?keywords=praia&tipo=image&dimensao=horizontal&filters=remove-background,fundo-azul" \\
  -H "X-Api-Key: ${apikey}"`;

  const downloadCurl = `# A URL vem de url_download da busca. NÃO precisa de apikey (URL-capacidade).
curl -L "${BASE}/v1/files/<id>/download" -o arquivo.jpg`;

  return (
    <>
      <PageHead
        title="Documentação"
        eyebrow="API"
        desc="Referência viva da API consumidor. Exemplos prontos com a sua apikey."
      />

      {/* Seletor de empresa (super) */}
      {isSuper && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: 20,
            padding: '12px 14px',
            background: 'var(--bg-1)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-md)',
            flexWrap: 'wrap'
          }}
        >
          <span style={{ fontSize: 13, color: 'var(--text-2)' }}>Empresa nos exemplos:</span>
          {companies.length === 0 ? (
            <span style={{ fontSize: 13, color: 'var(--text-3)' }}>
              Nenhuma empresa com apikey ativa. Crie uma em Empresas.
            </span>
          ) : (
            <select
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value)}
              className="focusable"
              style={{
                padding: '6px 10px',
                fontSize: 13,
                border: '1px solid var(--border)',
                borderRadius: 'var(--r-sm)',
                background: 'var(--bg-0)',
                color: 'var(--text-0)'
              }}
            >
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome}
                </option>
              ))}
            </select>
          )}
          {apikey !== '<SUA_APIKEY>' && (
            <Badge color="neutral" mono>
              {apikey.slice(0, 12)}…
            </Badge>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 28, alignItems: 'flex-start' }}>
        {/* Índice */}
        <nav
          style={{
            position: 'sticky',
            top: 0,
            flexShrink: 0,
            width: 170,
            fontSize: 13,
            display: 'flex',
            flexDirection: 'column',
            gap: 6
          }}
        >
          <span className="eyebrow">Nesta página</span>
          {[
            ['intro', 'Introdução'],
            ['auth', 'Autenticação'],
            ['search', 'GET /v1/search'],
            ['download', 'Download'],
            ['errors', 'Códigos de erro'],
            ['console', 'Console (testar)']
          ].map(([id, label]) => (
            <a key={id} href={`#${id}`} style={{ color: 'var(--text-2)', textDecoration: 'none' }}>
              {label}
            </a>
          ))}
        </nav>

        {/* Conteúdo */}
        <div style={{ flex: 1, minWidth: 0, maxWidth: 760, fontSize: 13.5, lineHeight: 1.6, color: 'var(--text-1)' }}>
          <Section id="intro" title="Introdução">
            <p style={{ marginBottom: 10 }}>
              A API de busca <b>sempre tenta retornar um arquivo compatível</b>: procura no
              cache da sua empresa, rankeia por relevância e, se não houver, busca em fontes
              externas ou gera via IA (Get_more) — populando o cache no processo.
            </p>
            <p>
              Base: <code className="mono">{BASE}/v1</code> · Conteúdo JSON · pt-BR.
            </p>
          </Section>

          <Section id="auth" title="Autenticação">
            <p style={{ marginBottom: 10 }}>
              A busca exige o header <code className="mono">X-Api-Key</code> (uma por empresa).
              O endpoint de <b>download é público por URL-capacidade</b>: o id é um UUID
              impossível de adivinhar, obtido apenas via busca autenticada (ADR-0018).
            </p>
          </Section>

          <Section id="search" title="GET /v1/search">
            <p style={{ marginBottom: 10 }}>Parâmetros principais:</p>
            <ul style={{ margin: '0 0 12px 18px' }}>
              <li><code className="mono">tipo</code> (obrigatório) — image, video, audio, document, presentation, spreadsheet, data, other.</li>
              <li><code className="mono">keywords</code> — 0+ palavras-chave (pré-filtra a busca local).</li>
              <li><code className="mono">descricao</code> — texto livre; alimenta o ranking (FTS/IA).</li>
              <li><b>Informe ao menos <code className="mono">keywords</code> ou <code className="mono">descricao</code>.</b></li>
              <li><code className="mono">quantidade</code> — default 1, máx 20.</li>
              <li><code className="mono">ranker</code> — auto (default), local_only, ai_only, off.</li>
              <li><code className="mono">provider</code> — slug de um provider: filtra só essa origem e, no Get_more, aciona só ele.</li>
              <li><code className="mono">dimensao</code> — <code className="mono">quadrado</code>, <code className="mono">horizontal</code> ou <code className="mono">vertical</code>. Repassada à fonte externa (ex.: orientação no Pexels) e gravada no arquivo.</li>
              <li><code className="mono">filters</code> — cadeia de filtros aplicada <b>na ordem</b> sobre a imagem retornada, ex.: <code className="mono">filters=remove-background,fundo-azul</code>. Cada valor é o <i>slug</i> de um provider tipo <code className="mono">filter</code>. O resultado é cacheado por etapa.</li>
              <li><code className="mono">promptAi</code> — nome de um template de prompt para geração via IA. Resolve na sua empresa, senão no sistema; vazio usa o template padrão. Inexistente → <code className="mono">TEMPLATE_NOT_FOUND</code> (400). Gerencie em <b>Templates</b>.</li>
              <li><code className="mono">force_new</code> — <code className="mono">true</code> pula o cache e <b>força um novo Get_more</b> (gera/baixa de novo mesmo havendo no banco). Ignora <code className="mono">Idempotency-Key</code>. Use com <code className="mono">provider=</code> para forçar geração AI. ⚠️ custo a cada chamada.</li>
              <li><code className="mono">force_dimensao</code> — <code className="mono">quadrado|horizontal|vertical</code>: na busca local só considera arquivos com essa orientação; se não houver, dispara Get_more (com essa orientação).</li>
              <li><code className="mono">human_revision</code>, <code className="mono">force_output</code>, <code className="mono">recent_window</code>, <code className="mono">extensoes_aceitas</code>.</li>
            </ul>
            <p style={{ fontSize: 12.5, color: 'var(--text-2)', marginBottom: 12 }}>
              <b>Geração via IA:</b> quando não há arquivo no cache nem em fontes de stock, o sistema
              pode <b>gerar</b> a imagem (provider AI). A chamada é <b>síncrona</b> (aguarda a geração,
              com timeout). Buscas idênticas seguintes reaproveitam o <b>cache</b> (não regeram). Se o
              gerador falhar (ex.: sem crédito), a resposta traz <code className="mono">codigo: "GET_MORE_FAILED"</code>
              e um campo <code className="mono">detalhe: [&#123; provider, erro &#125;]</code> com a causa real.
            </p>
            <p style={{ marginBottom: 8 }}>Com dimensão e filtros:</p>
            <CodeBlock code={filtersCurl} />
            <p style={{ fontSize: 12.5, color: 'var(--text-2)', marginBottom: 12 }}>
              Quando há <code className="mono">filters</code>, a resposta inclui <code className="mono">filtros_aplicados</code> e o
              arquivo traz <code className="mono">dimensao</code>/<code className="mono">largura</code>/<code className="mono">altura</code>. Erros de filtro:
              <code className="mono">FILTER_NOT_FOUND</code> (400), <code className="mono">FILTER_NOT_APPLICABLE</code> (400).
            </p>
            <p style={{ marginBottom: 8 }}>Exemplo:</p>
            <CodeBlock code={searchCurl} />
            <p style={{ marginBottom: 8 }}>Resposta:</p>
            <CodeBlock code={searchResp} />
          </Section>

          <Section id="download" title="Download de arquivo">
            <p style={{ marginBottom: 10 }}>
              Use o <code className="mono">url_download</code> retornado pela busca. Acesso
              direto, sem apikey:
            </p>
            <CodeBlock code={downloadCurl} />
          </Section>

          <ConsoleSection apikey={apikey} />

          <Section id="errors" title="Códigos de erro">
            <p style={{ marginBottom: 10 }}>
              Erros seguem <code className="mono">{'{ "erro": 1, "msg": "...", "codigo": "..." }'}</code>.
            </p>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--text-2)' }}>
                  <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)' }}>HTTP</th>
                  <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)' }}>código</th>
                  <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)' }}>quando</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['401', 'INVALID_APIKEY', 'apikey ausente/inválida/expirada'],
                  ['400', 'INVALID_PARAMS', 'parâmetros inválidos'],
                  ['422', 'NO_APPROVED_FILE', 'human_revision sem aprovados e sem force_output'],
                  ['429', 'RATE_LIMITED', 'limite de requisições (circuit breaker)'],
                  ['400', 'TEMPLATE_NOT_FOUND', 'promptAi informado não existe (empresa nem sistema)'],
                  ['502', 'GET_MORE_FAILED', 'todos os providers externos falharam (ver detalhe)'],
                  ['503', 'NO_PROVIDER_AVAILABLE', 'nenhum provider compatível ativo']
                ].map((r) => (
                  <tr key={r[1]}>
                    <td className="mono" style={{ padding: '6px 8px', borderBottom: '1px solid var(--border-soft)' }}>{r[0]}</td>
                    <td className="mono" style={{ padding: '6px 8px', borderBottom: '1px solid var(--border-soft)', color: 'var(--text-0)' }}>{r[1]}</td>
                    <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--border-soft)', color: 'var(--text-2)' }}>{r[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        </div>
      </div>
    </>
  );
}
